import React from "react";
import { Link } from "react-router-dom";
import { Wordmark } from "./Logo";
import { Instagram, Mail, Phone, MapPin, MessageSquare } from "lucide-react";

const CONTACT = (c) => [
  [MessageSquare, "Customer Service", c.hours || "Mon–Sat, 10am–6pm IST"],
  [Phone, "Call / WhatsApp", c.phone || "+91 99712 20816"],
  [Mail, "Get in Touch", c.email || "hello@havenhives.com"],
  [MapPin, "Ships From", c.address || "Gurugram, Haryana, India"],
];

const COLUMNS = [
  ["Company", [["Our Story", "/about"], ["Contact", "/contact"], ["FAQs", "/faqs"], ["Track Order", "/track-order"], ["My Orders", "/account"]]],
  ["Policies", [["Shipping & Delivery", "/policies/shipping"], ["Returns & Exchange", "/policies/returns"], ["Privacy Policy", "/policies/privacy"], ["Terms of Service", "/policies/terms"], ["Secure Payment", "/policies/shipping"]]],
  ["Shop", [["Water Bottles", "/collections/water-bottles"], ["Lunchboxes & Bento", "/collections/lunchboxes"], ["Stationery & Bundles", "/collections/stationery"], ["Kids' Luggage", "/collections/luggage"], ["Gift Sets", "/collections/gifts"], ["New Arrivals", "/collections/new-arrivals"]]],
];

export default function Footer({ settings }) {
  const [email, setEmail] = React.useState("");
  const [msg, setMsg] = React.useState("");
  const c = settings?.contact || {};

  const subscribe = async (e) => {
    e.preventDefault();
    try {
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/newsletter`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, marketing_consent: true }),
      });
      const d = await r.json();
      setMsg(d.detail || d.message);
    } catch { setMsg("Something went wrong — please try again."); }
  };

  return (
    <footer className="bg-forest text-white" data-testid="site-footer">
      <div className="mx-auto max-w-[1700px] px-5 sm:px-8 xl:px-14">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 py-9 border-b border-white/10">
          {CONTACT(c).map(([Icon, title, value]) => (
            <div key={title} className="flex items-start gap-4">
              <Icon className="w-6 h-6 mt-0.5 shrink-0" strokeWidth={1.5} />
              <div className="text-sm">
                <p>{title}</p>
                <p className="text-white/80 mt-1">{value}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="grid lg:grid-cols-12 gap-10 py-12">
          <div className="lg:col-span-5">
            <h2 className="font-display text-3xl sm:text-4xl">Join Our Newsletter</h2>
            <p className="text-sm text-white/80 mt-3">Sign up to our newsletter & receive 10% off your first order.</p>
            <form onSubmit={subscribe} className="mt-5 flex gap-2 max-w-md">
              <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="footer-newsletter-email"
                placeholder="Enter your email" aria-label="Email address for newsletter"
                className="field !bg-white/10 !text-white placeholder:!text-white/50 focus:!bg-white/15" />
              <button type="submit" data-testid="footer-newsletter-submit" className="btn-primary bg-white text-charcoal hover:bg-surface shrink-0">Sign Up</button>
            </form>
            <p className="text-xs text-white/60 mt-3">By subscribing you agree to the <Link to="/policies/terms" className="underline">Terms of Use</Link> & <Link to="/policies/privacy" className="underline">Privacy Policy</Link>.</p>
            {msg && <p role="status" className="text-xs text-sky mt-2">{msg}</p>}
          </div>
          <div className="lg:col-span-7 grid grid-cols-2 sm:grid-cols-3 gap-8 lg:pl-10">
            {COLUMNS.map(([title, links]) => (
              <nav key={title} aria-label={`${title} links`}>
                <h3 className="font-display text-xl mb-4">{title}</h3>
                <ul className="space-y-1 text-sm text-white/80">
                  {links.map(([label, to]) => <li key={label}><Link className="inline-block py-1.5 hover:text-white transition-colors" to={to}>{label}</Link></li>)}
                </ul>
              </nav>
            ))}
          </div>
        </div>

        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5 py-6 border-t border-white/10">
          <div className="flex items-center gap-3 flex-wrap text-[11px] text-white/80">
            <span>India (INR ₹)</span>
            <span className="text-white/30">·</span>
            {["UPI", "Visa", "Mastercard", "RuPay", "Net Banking", "COD"].map((p) => (
              <span key={p} className="bg-white text-charcoal px-2 py-1 rounded">{p}</span>
            ))}
          </div>
          <a href="https://instagram.com/havenhives_" target="_blank" rel="noopener noreferrer" data-testid="footer-instagram" aria-label="Instagram"
            className="w-11 h-11 rounded-full border border-white/20 flex items-center justify-center hover:bg-white/10 transition-colors">
            <Instagram className="w-4 h-4" />
          </a>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 justify-between items-center pb-7 text-xs text-white/70">
          <p className="flex items-center gap-3"><Wordmark className="h-7 opacity-90" /> © {new Date().getFullYear()} Haven Hives. Little things, big happiness.</p>
          <p className="flex gap-5"><Link to="/policies/terms" className="inline-block py-2 hover:text-white">Terms of Service</Link><Link to="/policies/privacy" className="inline-block py-2 hover:text-white">Privacy Policy</Link></p>
        </div>
      </div>
    </footer>
  );
}
